<template>
  <div
    v-if="badgeText && bannerText"
    class="hinweis"
    :class="[`hinweis--${reductionType}`, { 'hinweis--euro-voucher': isEuroVoucher }]"
  >
    <Badge class="hinweis__badge">
      <span v-html="badgeText" />
    </Badge>
    <Paragraph
      class="hinweis__text"
      :text="bannerText"
    />
  </div>
</template>

<script setup lang="ts">
import { useNuxtApp } from '#imports';
import { Badge, Paragraph } from '@i22-td-smarthome/component-library';
import { computed, defineComponent } from 'vue';
import { humanizePrice, defaultPriceOptions } from '@i22/rocket';

defineComponent({ name: 'Hinweis' });

const store = useNuxtApp().$store;

const couponInfo = computed(() => store.getters['coupon/couponInfo']);
const reductionValue = computed(() => store.getters['coupon/reductionValue']);

const reductionType = computed(() => {
  switch (couponInfo.value?.reductionMethodCode) {
    case 'RS':
    case 'RT':
      return 'absolute';
    case 'RP':
      return 'percentage';
    case 'PS':
      return 'newPrice';
    default:
      return '';
  }
});

const hasProductLimitation = computed(() => couponInfo.value?.typeCode === '01');

const isEuroVoucher = computed(
  () => reductionType.value === 'absolute' && couponInfo.value?.type === 'voucher'
);

const badgeText = computed(() => {
  if (!reductionValue.value || !reductionType.value) return '';
  if (reductionType.value === 'percentage') return `${reductionValue.value} % Rabatt!`;
  if (reductionType.value === 'absolute') {
    return couponInfo.value?.type === 'coupon'
      ? `${reductionValue.value} € Rabatt!`
      : 'Jetzt shoppen und Guthaben einlösen!';
  }
  if (reductionType.value === 'newPrice') return 'Aktionspreis';
  return '';
});

const bannerText = computed(() => {
  if (!reductionValue.value || !reductionType.value) return '';
  if (reductionType.value === 'percentage')
    return `<strong>${reductionValue.value} %</strong> Rabatt auf ${
      hasProductLimitation.value ? 'ein Produkt' : 'alle Produkte'
    } sichern! Der Rabatt wird im Warenkorb abgezogen.`;

  const humanizedPrice = humanizePrice(reductionValue.value, defaultPriceOptions);
  if (reductionType.value === 'absolute') {
    if (couponInfo.value?.type === 'coupon') {
      return hasProductLimitation.value
        ? `<strong>${humanizedPrice}</strong> Rabatt auf ein Produkt sichern! Der Rabatt wird im Warenkorb abgezogen.`
        : `Jetzt <strong>${humanizedPrice}</strong> Rabatt sichern! Der Rabatt wird im Warenkorb abgezogen.`;
    } else {
      return `Dein Gutschein-Guthaben beträgt <strong>${humanizedPrice}</strong>. Der Rabatt wird im Warenkorb abgezogen.`;
    }
  }
  if (reductionType.value === 'newPrice') {
    return `Jetzt Dein Wunsch-Produkt auf <strong>${humanizedPrice}</strong> reduzieren.`;
  }
  return '';
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.hinweis {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: color(magenta, 1000);
  color: color(white);
  border-radius: 10px;
  position: relative;
  margin-bottom: $grid-base * 5;
  padding: $grid-base;

  strong {
    font-weight: 800;
  }

  &__text {
    margin-bottom: 0;
    text-align: center;
    max-width: 50%;

    @include mq($mq-sm) {
      @include font-size(24px, 29px);
      max-width: 40%;
    }
  }
  &__badge {
    --badge-xs-size: 1.1em;
    --badge-sm-size: 1.5em;

    position: absolute;
    right: 75%;
    :deep(.badge__content) {
      font-weight: 800;

      padding: 0.8em;
      @include mq($mq-sm) {
        padding: 1.2em;
      }
    }
  }

  &--euro-voucher,
  &--newPrice {
    .hinweis {
      &__text {
        @include mq(false, $mq-sm - 1) {
          max-width: none;
        }
      }

      &__badge {
        @include mq(false, $mq-sm - 1) {
          display: none;
        }
      }
    }
  }

  &--newPrice {
    .hinweis {
      &__badge {
        --badge-sm-size: 1.2em;
      }
    }
  }
}
</style>
